import { createBrowserRouter, createHashRouter, Navigate, Outlet, RouteObject, useLocation } from 'react-router-dom';
// layouts
import CompactLayout from '../layouts/compact';
// config
import { PATH_AFTER_LOGIN } from '../config';

import App from 'src/App';

import { ElementType, lazy, Suspense } from 'react';
import LoadingScreen from 'src/components/loading-screen';
import { LoadingSpinner } from 'src/components/loading-spinner';
import MobileLayout from 'src/layouts/mobile';
import ErrorPage from 'src/pages/ErrorPage';
import NotAllowedPage from 'src/pages/NotAllowedPage';
import CartGuard from 'src/features/cart/CartGuard';
import { CartProvider } from 'src/features/cart/hooks/CartContext';
import PayGuard from 'src/features/order/PayGuard';
import GuestGuard from 'src/features/auth/GuestGuard';
import ProfileGuard from 'src/features/auth/ProfileGuard';
import { Page404Custom, Page404PaymentLink } from '../pages/Page404';
import OrderGuard from 'src/features/cart/OrderGuard';
import Home from 'src/pages/Home';

/**
 * This will show a full screen spinner while the component is loading.
 * It is meant to we used for pages
 */
const withAmbiguLoadingScreen = (Component: ElementType) => (props: any) =>
(
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

/**
 * This will show a spinner while the component is loading
 * It is meant to we used for components where you do not want to block the whole page
 */
const withBrandLoadingScreen = (Component: ElementType) => (props: any) =>
(
  <Suspense fallback={<LoadingSpinner />}>
    <Component {...props} />
  </Suspense>
);

const LazyPage404 = withBrandLoadingScreen(lazy(() => import('../pages/Page404')));
const LazyMenuPage = withBrandLoadingScreen(lazy(() => import('src/features/menu/MenuPage')));
const LazyCartPage = withBrandLoadingScreen(lazy(() => import('src/features/cart/CartPage')));
const LazyArticlePage = withBrandLoadingScreen(lazy(() => import('src/features/article/ArticlePage')));
const LazyInitialMenuPage = withBrandLoadingScreen(lazy(() => import('src/features/initial-menu/InitialMenuPage')));
const LazyShopSelector = withBrandLoadingScreen(lazy(() => import('src/features/shop-selector/ShopSelectorPage')));
const LazyEnterAddress = withBrandLoadingScreen(lazy(() => import('src/features/enter-address/EnterAddressPage')));
const LazyLoginPage = withBrandLoadingScreen(lazy(() => import('src/features/cart/cart-login-phone/LoginPhone')));
const LazyCodePage = withBrandLoadingScreen(lazy(() => import('src/features/cart/cart-code-page/CartCodePage')));
const LazyDetailOrderPage = withBrandLoadingScreen(lazy(() => import('src/features/order/DetailOrderPage')));
const LazyTrackingOrderPage = withBrandLoadingScreen(lazy(() => import('src/features/tracking-order/TrackingOrderDeliveryPage')));
const LazyTrackingOrderTakeAawayPage = withBrandLoadingScreen(
  lazy(() => import('src/features/tracking-order/TrackingOrderTakeawayPage'))
);
const LazyProfilePage = withBrandLoadingScreen(lazy(() => import('src/features/profile/ProfilePage')));
const LazyVoucherPage = withBrandLoadingScreen(lazy(() => import('src/features/profile/VoucherPage')));
const LazyPersonalConfigurationPage = withBrandLoadingScreen(lazy(() => import('src/features/profile/PersonalConfigurationPage')));
const LazyPaymentPage = withBrandLoadingScreen(lazy(() => import('src/features/order/payment/PaymentPage')));
const LazyPaymentPageBizum = withBrandLoadingScreen(lazy(() => import('src/features/order/payment/PaymentPageBizum')));
const LazyOrderSuccessPage = withBrandLoadingScreen(lazy(() => import('src/features/order/OrderSuccessPage')));
const LazyOrderErrorPage = withBrandLoadingScreen(lazy(() => import('src/features/order/OrderErrorPage')));
const LazyAuthPage = withBrandLoadingScreen(lazy(() => import('src/features/auth/login/LoginPage')));
const LazyVerifyCodePage = withBrandLoadingScreen(lazy(() => import('src/features/auth/verify-code/VerifyCodePage')));

const LazyPreorderPage = withBrandLoadingScreen(lazy(() => import('src/features/preorder/PreorderPage')));
const LazyPaymentLinkPage = withBrandLoadingScreen(lazy(() => import('src/features/payment-link/PaymentLinkPage')));
const LazyPaymentSuccessPage = withBrandLoadingScreen(lazy(() => import('src/features/payment-link/sucess-page/PaymentSuccessPage')));
const LazyPaymentWarningPage = withBrandLoadingScreen(lazy(() => import('src/features/payment-link/warning-page/PaymentWarningPage')));

const HashGuard = () => {
  const { hash } = useLocation();

  if (hash) {
    const newUrl = hash.replace(/#\/?/, '');
    return <Navigate to={newUrl} replace />;
  }

  return <Navigate to={PATH_AFTER_LOGIN} replace />;
};

const HomeWithHashRedirect = () => {
  const { hash } = useLocation();
  if (hash) {
    const newUrl = hash.replace(/#\/?/, '');
    return <Navigate to={newUrl} replace />;
  }
 
  return <Home />;
};

const ROUTES: RouteObject[] = [
  {
    path: '/',
    element: <App />,
    errorElement: <Page404Custom />,
    children: [
      { element: <HomeWithHashRedirect />, index: true },
      // { element: <Navigate to="/404" replace />, index: true },
      {
        path: 'client/:slug_id/auth',
        element: (
          <GuestGuard>
            <Outlet />
          </GuestGuard>
        ),
        children: [
          {
            path: 'login',
            element: <LazyAuthPage />,
            errorElement: <ErrorPage />,
          },
          {
            children: [
              { path: 'verify', element: <LazyVerifyCodePage /> },
            ],
          },
          { element: <Navigate to="login" replace />, index: true },
        ],
      },
      {
        path: 'client/:slug_id/profile',
        errorElement: <LazyAuthPage />,
        element: (
          <ProfileGuard>
            <Outlet />
          </ProfileGuard>
        ),
        children: [
          { path: 'me', element: <LazyProfilePage /> },
          { path: 'vouchers', element: <LazyVoucherPage /> },
          { path: 'personal-configuration', element: <LazyPersonalConfigurationPage /> }
        ]
      },
      { path: 'client/:slug_id', element: <LazyInitialMenuPage /> },
      {
        path: 'client/:slug_id',
        errorElement: <ErrorPage />,
        element: (
            <MobileLayout />
        ),
        children: [
          {
            path: 'dinein',
            errorElement: <ErrorPage />,
            element: <MobileLayout />,
            children: [
              { element: <Navigate to='dinein' replace />, index: true },              
              {
                path: ':shop_id/:table_id/menu/:menu_id',
                element: <LazyMenuPage type="dinein" />,
              },
              {
                path: ':shop_id/:table_id/article/:article_id',
                element: <LazyArticlePage type="dinein" />,
              },        
              { path: ':shop_id/:table_id/shop-selector', element: <LazyShopSelector type="dinein" /> },                                                
              { path: ':order_id/success', element: <LazyOrderSuccessPage type="dinein" /> },
              { path: 'tracking-order/:order_id', element: <LazyTrackingOrderTakeAawayPage /> },
              {
                path: ':shop_id/:table_id/menu/:menu_id',
                element: (
                  <CartProvider>
                    <OrderGuard type='dinein'>
                      <Outlet />
                    </OrderGuard>
                  </CartProvider>
                ),
                children: [
                  {
                    path: 'cart',
                    element: <LazyCartPage type="dinein" />
                  },
                  {
                    path: 'login',
                    element: <LazyLoginPage type="dinein" />
                  },
                  {
                    path: 'code',
                    element: <LazyCodePage type="dinein" />
                  },
                  {
                    path: 'detail-order/:order_id',
                    element: <LazyDetailOrderPage type="dinein" />
                  },
                  {
                    path: 'detail-order/:order_id/payment',
                    element: <LazyPaymentPage type="dinein" />
                  },
                  {
                    path: 'detail-order/:order_id/paymentBizum',
                    element: <LazyPaymentPageBizum type="dinein" />
                  }
                ]
              },              
              {
                path: ':shop_id/:table_id/menu/:menu_id',
                element: (
                  <CartProvider>
                    <PayGuard>
                      <Outlet />
                    </PayGuard>
                  </CartProvider>
                ),
                children: [
                  {
                    path: 'error',
                    element: <LazyOrderErrorPage />
                  },
                ]
              }, 
              {
                path: 'cart',
                element: <LazyCartPage type="dinein" />
              },          
            ],
          },
          {
            path: 'takeaway',
            errorElement: <ErrorPage />,
            element: <MobileLayout />,
            children: [
              { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
              {
                path: ':shop_id/menu/:menu_id',
                element: <LazyMenuPage type="takeaway" />,
              },              
              {
                path: ':shop_id/article/:article_id',
                element: <LazyArticlePage type="takeaway" />,
              },                         
              { path: 'shop-selector', element: <LazyShopSelector type="takeaway" /> },              
              { path: ':order_id/success', element: <LazyOrderSuccessPage type="takeaway" /> },
              { path: 'tracking-order/:order_id', element: <LazyTrackingOrderTakeAawayPage /> },
              {
                path: ':shop_id/menu/:menu_id',
                element: (
                  <CartProvider>
                    <OrderGuard type='takeaway'>
                      <Outlet />
                    </OrderGuard>
                  </CartProvider>
                ),
                children: [
                  {
                    path: 'cart',
                    element: <LazyCartPage type="takeaway" />
                  },
                  {
                    path: 'login',
                    element: <LazyLoginPage type="takeaway" />
                  },
                  {
                    path: 'code',
                    element: <LazyCodePage type="takeaway" />
                  },
                  {
                    path: 'detail-order/:order_id',
                    element: <LazyDetailOrderPage type="takeaway" />
                  },
                  {
                    path: 'detail-order/:order_id/payment',
                    element: <LazyPaymentPage type="takeaway" />
                  },
                  {
                    path: 'detail-order/:order_id/paymentBizum',
                    element: <LazyPaymentPageBizum type="takeaway" />
                  }
                ]
              },
              {
                path: ':shop_id/menu/:menu_id/:table_id',
                element: (
                  <CartProvider>
                    <OrderGuard type='takeaway'>
                      <Outlet />
                    </OrderGuard>
                  </CartProvider>
                ),
                children: [
                  {
                    path: 'cart',
                    element: <LazyCartPage type="takeaway" />
                  },
                  {
                    path: 'login',
                    element: <LazyLoginPage type="takeaway" />
                  },
                  {
                    path: 'code',
                    element: <LazyCodePage type="takeaway" />
                  },
                  {
                    path: 'detail-order/:order_id',
                    element: <LazyDetailOrderPage type="takeaway" />
                  },
                  {
                    path: 'detail-order/:order_id/payment',
                    element: <LazyPaymentPage type="takeaway" />
                  },
                  {
                    path: 'detail-order/:order_id/paymentBizum',
                    element: <LazyPaymentPageBizum type="takeaway" />
                  }
                ]
              },
              {
                path: ':shop_id/menu/:menu_id',
                element: (
                  <CartProvider>
                    <PayGuard>
                      <Outlet />
                    </PayGuard>
                  </CartProvider>
                ),
                children: [
                  {
                    path: 'error',
                    element: <LazyOrderErrorPage />
                  },
                ]
              },
              {
                path: 'cart',
                element: <LazyCartPage type="takeaway" />
              },              
            ],
          },
          {
            path: 'show-menu',
            errorElement: <ErrorPage />,
            element: <MobileLayout />,
            children: [
              { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
              {
                path: ':shop_id/menu/:menu_id',
                element: <LazyMenuPage type="show_menu" />,
              },
              { path: 'shop-selector', element: <LazyShopSelector type="show_menu" /> },
            ],
          },
          {
            path: 'delivery',
            errorElement: <ErrorPage />,
            element: <MobileLayout />,
            children: [
              { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
              {
                path: ':shop_id/menu/:menu_id',
                element: <LazyMenuPage type="delivery" />,
              },
              {
                path: ':shop_id/article/:article_id',
                element: <LazyArticlePage type="delivery" />,
              },
              { path: 'enter-address', element: <LazyEnterAddress /> },
              { path: ':order_id/success', element: <LazyOrderSuccessPage type="delivery" /> },
              { path: 'tracking-order/:order_id', element: <LazyTrackingOrderPage type="delivery" /> },
              {
                path: ':shop_id/menu/:menu_id',
                element: (
                  <CartProvider>
                    <PayGuard>
                      <Outlet />
                    </PayGuard>
                  </CartProvider>
                ),
                children: [
                  {
                    path: 'error',
                    element: <LazyOrderErrorPage />
                  },
                ]
              },
              {
                path: ':shop_id/menu/:menu_id',
                element: (
                  <CartProvider>
                    <OrderGuard type='delivery'>
                      <Outlet />
                    </OrderGuard>
                  </CartProvider>
                ),
                children: [
                  {
                    path: 'cart',
                    element: <LazyCartPage type="delivery" />
                  },
                  {
                    path: 'login',
                    element: <LazyLoginPage type="delivery" />
                  },
                  {
                    path: 'code',
                    element: <LazyCodePage type="delivery" />
                  },
                  {
                    path: 'detail-order/:order_id',
                    element: <LazyDetailOrderPage type="delivery" />
                  },
                  {
                    path: 'detail-order/:order_id/payment',
                    element: <LazyPaymentPage type="delivery" />
                  },
                  {
                    path: 'detail-order/:order_id/paymentBizum',
                    element: <LazyPaymentPageBizum type="delivery" />
                  }
                ]
              },
            ],
          },
        ]
      },
      { path: 'client/:slug_id/preorder/date-time-selector', element: <LazyPreorderPage /> },
      {
        path: 'client/:slug_id/guest/payment-link/:order_id',
        element: <LazyPaymentLinkPage />,
        errorElement: <Page404PaymentLink />
      },
      {
        path: 'client/:slug_id/guest/success-payment-link/:order_id',
        element: <LazyPaymentSuccessPage />,
        errorElement: <Page404PaymentLink />
      },
      {
        path: 'client/:slug_id/guest/warning-payment-link/:order_id',
        element: <LazyPaymentWarningPage />,
        errorElement: <Page404PaymentLink />
      },
      {
        element: <MobileLayout />,
        children: [
          { path: '404', element: <LazyPage404 /> },
          { path: 'not-allowed', element: <NotAllowedPage /> },
        ],
      },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
];

export const router = createBrowserRouter(ROUTES);
